import { clone, isNil, merge } from "lodash";

import { Precision } from "../../../../types";
import dateAtEvenSpanBeforeDate from "./dateAtEvenSpanBeforeDate";
import dateByAddingSpan from "./dateByAddingSpan";
import { calendarPrecalculations } from "./dateInternalCalculations";
import dateSubtractionFunctions from "./dateSubtractionFunctions";
import dateTickIntervals from "./dateTickIntervals";
import dateWithComponents from "./dateWithComponents";
import dateWithTimestamp from "./dateWithTimestamp";
import secondsInDuration from "./secondsInDuration";
import {
  isTimespanNegative,
  timespanForComponents,
  timespanForPrecision,
  timespanForPrecisionAndUnits
} from "./timespanFunctions";

/**
 * @param {DateRecord} date
 */
export const isDateValid = date => Boolean(date && !isNil(date.timestamp));

export const doesDateHaveTime = date =>
  Boolean(isDateValid(date) && (date.hour > 0 || date.minute > 0 || date.second > 0));

/**
 * @param {DateRecord} dateA
 * @param {DateRecord} dateB
 */
const areDatesSame = (dateA, dateB) =>
  (!isDateValid(dateA) && !isDateValid(dateB)) ||
  (isDateValid(dateA) &&
    isDateValid(dateB) &&
    dateA.timestamp === dateB.timestamp &&
    dateA.precision === dateB.precision);

/**
 * Include equal
 * @param {CalendarRecord} calendar
 * @param {DateRecord} dateA
 * @param {DateRecord} dateB
 */
export const isDateEarlierThan = (calendar, dateA, dateB) =>
  !isTimespanNegative(dateSubtractionFunctions.timespanBetweenDates(calendar, dateA, dateB));

export const javascriptDateToAeonDate = (calendar, jsDate) => {
  return dateWithComponents(
    calendar,
    calendar.eras.size - 1,
    jsDate.getFullYear(),
    jsDate.getMonth() + 1,
    jsDate.getDate(),
    jsDate.getHours(),
    jsDate.getMinutes(),
    jsDate.getSeconds(),
    Precision.Second
  );
};

const dateToTimestamp = (calendar, date) => date.get("timestamp");

const timestampForNow = calendar => dateForNow(calendar).timestamp;
const dateForNow = calendar => {
  if (isNil(calendar.overrideNowTimestamp)) {
    return javascriptDateToAeonDate(calendar, new Date());
  }
  return dateWithTimestamp(calendar, calendar.overrideNowTimestamp);
};

const timestampForToday = calendar => dateForToday(calendar).timestamp;
const dateForToday = calendar =>
  dateAtEvenSpanBeforeDate(calendar, dateForNow(calendar), timespanForPrecisionAndUnits(Precision.Day, 1));

const dateForTodayUnlessOverride = calendar => {
  if (isNil(calendar.overrideNowTimestamp)) {
    return dateForToday(calendar);
  }
  return dateWithTimestamp(calendar, calendar.overrideNowTimestamp);
};

const dateAtStartOfWeekFromDate = (calendar, date) =>
  dateAtEvenSpanBeforeDate(calendar, date, timespanForPrecision(Precision.Week));
const dateForThisWeek = calendar => dateAtStartOfWeekFromDate(calendar, dateForToday(calendar));

const approxSecondsInTimespan = (calendar, span) => {
  const precalculations = calendarPrecalculations(calendar);
  return (
    span.years * precalculations.avgSecondsInYear +
    span.months * precalculations.avgSecondsInMonth +
    span.weeks * calendar.weekdays.size * 24 * 3600 +
    span.days * 24 * 3600 +
    span.hours * 3600 +
    span.minutes * 60 +
    span.seconds
  );
};

const roundedDateForScale = (calendar, date, unitScale) => {
  const precalculations = calendarPrecalculations(calendar);

  const testUnitScale = unitScale;

  const secondsCutoff = 30;
  if (testUnitScale < secondsCutoff) {
    const units = unitScale;
    const rounded = units <= 1 ? 1 : units <= 2 ? 2 : units <= 5 ? 5 : units <= 10 ? 10 : units <= 15 ? 15 : 30;
    return date.merge({
      second: Math.floor(date.second / rounded) * rounded,
      precision: Precision.Second
    });
  }

  const minutesCutoff = 1800;
  if (testUnitScale < minutesCutoff) {
    const units = unitScale / 60;
    const rounded = units <= 1 ? 1 : units <= 2 ? 2 : units <= 5 ? 5 : units <= 10 ? 10 : units <= 15 ? 15 : 30;
    return date.merge({
      minute: Math.floor(date.minute / rounded) * rounded,
      second: 0,
      precision: Precision.Minute
    });
  }

  const hoursInDay = calendar.hoursInDay;
  const hoursCutoff = (hoursInDay / 2) * 3600;
  if (testUnitScale < hoursCutoff) {
    const units = unitScale / 3600;
    // Round to a factor of hoursInDay (e.g. 24 = 1, 2, 3, 4, 6, 12)
    let rounded = 1;
    for (rounded = 1; rounded < hoursCutoff; rounded++) {
      if (hoursInDay % rounded === 0) {
        if (units < rounded) {
          break;
        }
      }
    }
    return date.merge({
      hour: Math.floor(date.hour / rounded) * rounded,
      minute: 0,
      second: 0,
      precision: Precision.Hour
    });
  }

  const daysCutoff = (precalculations.avgDaysInMonth / 2) * precalculations.secondsInDay;
  if (testUnitScale < daysCutoff) {
    return date.merge({
      hour: 0,
      minute: 0,
      second: 0,
      precision: Precision.Day
    });
  }

  const monthsCutoff = (calendar.months.count() / 2) * precalculations.avgSecondsInMonth;
  if (testUnitScale < monthsCutoff) {
    return date.merge({
      day: 1,
      hour: 0,
      minute: 0,
      second: 0,
      precision: Precision.Month
    });
  }

  return date.merge({
    month: 1,
    day: 1,
    hour: 0,
    minute: 0,
    second: 0,
    precision: Precision.Year
  });
};

const dateWithChangedPrecision = (calendar, date, precision) => {
  return dateAtEvenSpanBeforeDate(calendar, date, timespanForPrecision(precision));
};

const endDateWithChangedPrecision = (calendar, date, precision) => {
  const ts = timespanForPrecision(precision);
  const start = dateAtEvenSpanBeforeDate(calendar, date, ts);
  return dateByAddingSpan(calendar, start, ts);
};

// const isValidDate = _date => true;

export default merge(clone(dateSubtractionFunctions), {
  isDateValid,
  doesDateHaveTime,
  areDatesSame,
  isDateEarlierThan,
  javascriptDateToAeonDate,
  dateWithTimestamp,
  dateByAddingSpan,
  dateAtEvenSpanBeforeDate,
  dateTickIntervals,
  dateToTimestamp,
  timestampForNow,
  dateForNow,
  timestampForToday,
  dateForToday,
  dateForTodayUnlessOverride,
  dateForThisWeek,
  dateAtStartOfWeekFromDate,
  timespanForComponents,
  timespanForPrecision,
  approxSecondsInTimespan,
  roundedDateForScale,
  secondsInDuration,
  dateWithChangedPrecision,
  endDateWithChangedPrecision
});
